import React from 'react';
import { Box, Paper, Typography, IconButton, TextField, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from '../../i18n';
import { EditableField } from './hooks/useEditableField';

interface BioSectionProps {
  bio?: string;
  canEdit: boolean;
  saving: boolean;
  field: EditableField;
  onSave: () => void;
}

/**
 * Bio card for the User Profile page (STD-014 split out of UserProfilePage.tsx).
 * Edit/cancel state lives in the parent's `useEditableField`; this component is
 * purely presentational over that state plus the save callback.
 */
export const BioSection: React.FC<BioSectionProps> = ({ bio, canEdit, saving, field, onSave }) => {
  const { t } = useTranslation();

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('profile.bio')}</Typography>
        {canEdit && !field.isEditing && (
          <IconButton size="small" onClick={field.startEditing}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {field.isEditing && canEdit ? (
        <Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={field.value}
            onChange={(e) => field.setValue(e.target.value)}
            placeholder={t('profile.bioPlaceholder')}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={onSave} disabled={saving}>
              {t('common.save')}
            </Button>
            <Button onClick={field.cancelEditing}>{t('common.cancel')}</Button>
          </Box>
        </Box>
      ) : (
        <Typography color={bio ? 'text.primary' : 'text.secondary'}>
          {bio || t('profile.noBio')}
        </Typography>
      )}
    </Paper>
  );
};
